/**
 * Illustration model shared by every lesson subject (see docs/ART-BIBLE.md and
 * docs/art-rollout/AUTHORING.md). A figure is described ONCE as:
 *
 *   guides  light-blue construction shapes (stage 1), erased in stage 4
 *   layers  back-to-front list of
 *             region  closed shape with a colour role (outline in stage 2 or 3, colour in stage 5)
 *             ink     tapered detail stroke along a centre-line (whiskers, mouths, stripes)
 *             spot    small filled shape (eyes, sparkles, blush, cast shadows)
 *
 * Every element may carry a part name `p` ("head", "ears", "face"...). Lesson steps pick parts per
 * construction stage, and `compileArtSteps` turns them into ordinary animatable `Stroke`s that point
 * back at the figure (`Stroke.ref`). Colours are ROLES (e.g. "catFur"), never hex: each art style
 * maps roles to its own palette (`ART_PALETTES`).
 */

export type ArtStage = 1 | 2 | 3 | 4 | 5;
export const ART_STAGES: ArtStage[] = [1, 2, 3, 4, 5];

/** A = Storybook (default), B = Sticker pop, C = Soft pastel kawaii. */
export type ArtStyleId = 'A' | 'B' | 'C';
export const ART_STYLE_IDS: ArtStyleId[] = ['A', 'B', 'C'];

export type ArtTaper = 'both' | 'end' | 'start' | 'none';

interface ArtElement {
  /** Part name used by lesson steps (e.g. "head", "ears", "face"). */
  p?: string;
  /** Clip to another closed shape (e.g. stripes inside the body). */
  clip?: string;
  /**
   * Curriculum v2 scenes: hide the element INSIDE this closed shape (inverse clip) — a figure behind
   * stops at the silhouette of the figure in front (`composeFigure`).
   */
  clipOut?: string;
  /** Stage the element first appears in (defaults: region 2, ink 3, spot 5). */
  stage?: ArtStage;
}

export interface ArtRegion extends ArtElement {
  k: 'region';
  d: string;
  /** Colour role. */
  fill: string;
  /** Part of the outer silhouette (thick contour, sticker border). */
  sil?: boolean;
  /** Shade crescent offset override; false = no shade. */
  shade?: number | false;
  /** Rim highlight on the lit side (styles B / C). */
  rim?: boolean;
  /** Draw its own inner stroke (default true). */
  line?: boolean;
}

export interface ArtInk extends ArtElement {
  k: 'ink';
  /** Centre-line (open path). */
  d: string;
  /** Width multiplier of the style's detail width (default 1). */
  w?: number;
  taper?: ArtTaper;
  /** Colour role in the finished art; line art always uses the ink colour. */
  color?: string;
}

export interface ArtSpot extends ArtElement {
  k: 'spot';
  d: string;
  /** Colour role, or 'line' (ink colour) / 'shade' (the style's shade colour). */
  fill: string;
  opacity?: number;
  /** Blush-like spots scale their opacity by the style's blush factor. */
  blush?: boolean;
}

export type ArtLayer = ArtRegion | ArtInk | ArtSpot;

export interface ArtGuide {
  d: string;
  /** Axis / centre / eye line (dashed). */
  axis?: boolean;
  p?: string;
  /** Curriculum v2: one of the big "main" shapes still shown at L3 (mainShapes scaffold). */
  main?: boolean;
}

export interface ArtFigure {
  /** viewBox [w, h]; lesson subjects use 240 x 240. */
  vb: readonly [number, number];
  layers: ArtLayer[];
  guides: ArtGuide[];
  /** Soft ground shadow ellipse [cx, cy, rx, ry] (shown with colour). */
  ground?: readonly [number, number, number, number];
}

/** final = drawn in the new art system; pending = still the legacy line drawing / stub (fallback). */
export type ArtStatus = 'final' | 'pending';

/** Stage a layer first appears in. */
export const appearStage = (l: ArtLayer): ArtStage => l.stage ?? (l.k === 'region' ? 2 : l.k === 'ink' ? 3 : 5);
